/**
 * `<ContactProfileForm>` — shared form body used by Create (`mode: create`)
 * and Edit (`mode: edit`) flows. Validated client-side with Zod, mirroring
 * the server's `createContactSchema` / `updateContactSchema`.
 *
 * Per FD § 2.3 + § 2.2 Edit flow. Metadata is a JSON textarea with
 * parse validation on blur — NFR-Design § 7.
 */

import { Button, Input, Label, Textarea } from "@cometchat-ai/ui-dashboard";
import { Settings2, User } from "../icons";
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import type { ContactProfilePatch, CreateContactInput } from "../../lib/api/types";

const FIELD_INPUT =
  "h-9 rounded-lg text-[13px] md:text-[13px] placeholder:text-[color:var(--color-fg-subtle)]";

function SectionHeading({ icon: Icon, title }: { icon: typeof User; title: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <h3 className="text-sm font-semibold tracking-[0.01em] text-foreground">{title}</h3>
    </div>
  );
}

function Field({
  htmlFor,
  label,
  error,
  children,
}: {
  htmlFor: string;
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-[13px] font-medium text-foreground">
        {label}
      </Label>
      {children}
      {error ? (
        <p className="text-[13px] text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

const TRIM_OPTIONAL = z
  .string()
  .trim()
  .transform((v) => (v === "" ? undefined : v));

const profileSchema = z.object({
  display_name: TRIM_OPTIONAL.pipe(z.string().max(120).optional()),
  email: TRIM_OPTIONAL.pipe(z.string().email().max(254).optional()),
  phone: TRIM_OPTIONAL.pipe(z.string().max(32).optional()),
  avatar_url: TRIM_OPTIONAL.pipe(z.string().url().max(2048).optional()),
  locale: TRIM_OPTIONAL.pipe(z.string().max(16).optional()),
  timezone: TRIM_OPTIONAL.pipe(z.string().max(48).optional()),
});

const createSchema = profileSchema.extend({
  user_id: z.string().trim().min(1).max(128),
});

export type ContactProfileFormMode = "create" | "edit";

export interface ContactProfileFormValues {
  userId: string;
  displayName: string;
  email: string;
  phone: string;
  avatarUrl: string;
  locale: string;
  timezone: string;
  metadata: string;
}

const EMPTY: ContactProfileFormValues = {
  userId: "",
  displayName: "",
  email: "",
  phone: "",
  avatarUrl: "",
  locale: "",
  timezone: "",
  metadata: "",
};

export interface ContactProfileFormProps {
  mode: ContactProfileFormMode;
  initial?: Partial<ContactProfileFormValues>;
  /** Server-side error code (e.g. `user_id_taken`) mapped to field error inline. */
  serverError?: { code: string; message: string; details?: Record<string, unknown> };
  busy?: boolean;
  /** `id` for the `<form>` — lets an external submit button target it via `form=`. */
  id?: string;
  /** Render the built-in Cancel/Submit footer. Off when the page shell owns the
   *  action buttons (Final layout); defaults on for standalone use. */
  renderFooter?: boolean;
  onCancel: () => void;
  onSubmitCreate?: (input: CreateContactInput) => void;
  onSubmitEdit?: (patch: ContactProfilePatch) => void;
}

type FieldErrors = Partial<Record<keyof ContactProfileFormValues, string>>;

function parseMetadata(raw: string): { ok: true; value: Record<string, unknown> } | { ok: false } {
  if (raw.trim() === "") return { ok: true, value: {} };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return { ok: true, value: parsed as Record<string, unknown> };
    }
    return { ok: false };
  } catch {
    return { ok: false };
  }
}

export function ContactProfileForm(props: ContactProfileFormProps) {
  const { t } = useTranslation("contacts");
  const userIdId = useId();
  const displayNameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const avatarUrlId = useId();
  const localeId = useId();
  const timezoneId = useId();
  const metadataId = useId();
  const metadataErrId = useId();

  const [values, setValues] = useState<ContactProfileFormValues>({ ...EMPTY, ...props.initial });
  const [errors, setErrors] = useState<FieldErrors>({});

  function update<K extends keyof ContactProfileFormValues>(
    key: K,
    value: ContactProfileFormValues[K],
  ) {
    setValues((v) => ({ ...v, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  // Map server-side 409 / 422 errors onto the offending field.
  const userIdError =
    errors.userId ??
    (props.serverError?.code === "user_id_taken" ? t("create.errorTaken") : undefined);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const meta = parseMetadata(values.metadata);
    if (!meta.ok) {
      setErrors((cur) => ({ ...cur, metadata: t("form.metadataInvalid") }));
      return;
    }
    if (props.mode === "create" && props.onSubmitCreate) {
      const parsed = createSchema.safeParse({
        user_id: values.userId,
        display_name: values.displayName,
        email: values.email,
        phone: values.phone,
        avatar_url: values.avatarUrl,
        locale: values.locale,
        timezone: values.timezone,
      });
      if (!parsed.success) {
        const fe: FieldErrors = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path[0];
          if (path === "user_id") fe.userId = issue.message;
          if (path === "display_name") fe.displayName = issue.message;
          if (path === "email") fe.email = issue.message;
          if (path === "phone") fe.phone = issue.message;
          if (path === "avatar_url") fe.avatarUrl = issue.message;
          if (path === "locale") fe.locale = issue.message;
          if (path === "timezone") fe.timezone = issue.message;
        }
        setErrors(fe);
        return;
      }
      props.onSubmitCreate({
        userId: parsed.data.user_id,
        displayName: parsed.data.display_name,
        email: parsed.data.email,
        phone: parsed.data.phone,
        avatarUrl: parsed.data.avatar_url,
        locale: parsed.data.locale,
        timezone: parsed.data.timezone,
        metadata: meta.value,
      });
      return;
    }
    if (props.mode === "edit" && props.onSubmitEdit) {
      const parsed = profileSchema.safeParse({
        display_name: values.displayName,
        email: values.email,
        phone: values.phone,
        avatar_url: values.avatarUrl,
        locale: values.locale,
        timezone: values.timezone,
      });
      if (!parsed.success) {
        const fe: FieldErrors = {};
        for (const issue of parsed.error.issues) {
          const path = issue.path[0];
          if (path === "display_name") fe.displayName = issue.message;
          if (path === "email") fe.email = issue.message;
          if (path === "phone") fe.phone = issue.message;
          if (path === "avatar_url") fe.avatarUrl = issue.message;
          if (path === "locale") fe.locale = issue.message;
          if (path === "timezone") fe.timezone = issue.message;
        }
        setErrors(fe);
        return;
      }
      props.onSubmitEdit({
        displayName: parsed.data.display_name ?? null,
        email: parsed.data.email ?? null,
        phone: parsed.data.phone ?? null,
        avatarUrl: parsed.data.avatar_url ?? null,
        locale: parsed.data.locale ?? null,
        timezone: parsed.data.timezone ?? null,
        metadata: meta.value,
      });
    }
  }

  return (
    <form
      id={props.id}
      className="space-y-6"
      onSubmit={handleSubmit}
      data-testid="contact-profile-form"
      noValidate
    >
      {/* Basic information */}
      <section className="space-y-4">
        <SectionHeading icon={User} title={t("form.sectionBasic")} />

        {props.mode === "create" ? (
          <Field htmlFor={userIdId} label={t("form.userId")}>
            <Input
              id={userIdId}
              value={values.userId}
              onChange={(e) => update("userId", e.target.value)}
              placeholder={t("form.userIdHint")}
              aria-invalid={userIdError !== undefined}
              aria-describedby={userIdError ? `${userIdId}-err` : undefined}
              data-testid="contact-form-user-id"
              disabled={props.busy}
              className={FIELD_INPUT}
              autoFocus
            />
            {userIdError ? (
              <p id={`${userIdId}-err`} className="text-[13px] text-destructive" role="alert">
                {userIdError}
              </p>
            ) : null}
          </Field>
        ) : null}

        <div className="grid grid-cols-1 gap-4">
          <Field htmlFor={displayNameId} label={t("form.displayName")} error={errors.displayName}>
            <Input
              id={displayNameId}
              value={values.displayName}
              onChange={(e) => update("displayName", e.target.value)}
              placeholder="Ada Lovelace"
              aria-invalid={errors.displayName !== undefined}
              data-testid="contact-form-display-name"
              disabled={props.busy}
              className={FIELD_INPUT}
            />
          </Field>
          <Field htmlFor={emailId} label={t("form.email")} error={errors.email}>
            <Input
              id={emailId}
              type="email"
              value={values.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="ada@example.com"
              aria-invalid={errors.email !== undefined}
              data-testid="contact-form-email"
              disabled={props.busy}
              className={FIELD_INPUT}
            />
          </Field>
          <Field htmlFor={phoneId} label={t("form.phone")}>
            <Input
              id={phoneId}
              value={values.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="+1 415 555 0101"
              data-testid="contact-form-phone"
              disabled={props.busy}
              className={FIELD_INPUT}
            />
          </Field>
          <Field htmlFor={avatarUrlId} label={t("form.avatarUrl")} error={errors.avatarUrl}>
            <Input
              id={avatarUrlId}
              value={values.avatarUrl}
              onChange={(e) => update("avatarUrl", e.target.value)}
              placeholder="https://…"
              data-testid="contact-form-avatar-url"
              disabled={props.busy}
              aria-invalid={errors.avatarUrl !== undefined}
              className={FIELD_INPUT}
            />
          </Field>
        </div>
      </section>

      <div className="-mx-6 border-t border-dashed border-border" />

      {/* Advanced settings */}
      <section className="space-y-4">
        <SectionHeading icon={Settings2} title={t("form.sectionAdvanced")} />

        <div className="grid grid-cols-1 gap-4">
          <Field htmlFor={localeId} label={t("form.locale")}>
            <Input
              id={localeId}
              value={values.locale}
              onChange={(e) => update("locale", e.target.value)}
              placeholder="en-US"
              data-testid="contact-form-locale"
              disabled={props.busy}
              className={FIELD_INPUT}
            />
          </Field>
          <Field htmlFor={timezoneId} label={t("form.timezone")}>
            <Input
              id={timezoneId}
              value={values.timezone}
              onChange={(e) => update("timezone", e.target.value)}
              placeholder="America/Los_Angeles"
              data-testid="contact-form-timezone"
              disabled={props.busy}
              className={FIELD_INPUT}
            />
          </Field>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={metadataId} className="text-[13px] font-medium text-foreground">
            {t("form.metadata")}
          </Label>
          <Textarea
            id={metadataId}
            value={values.metadata}
            onChange={(e) => update("metadata", e.target.value)}
            placeholder='{"plan": "pro"}'
            rows={4}
            aria-invalid={errors.metadata !== undefined}
            aria-describedby={errors.metadata ? metadataErrId : undefined}
            data-testid="contact-form-metadata"
            disabled={props.busy}
            className="rounded-lg text-[13px] md:text-[13px] placeholder:text-[color:var(--color-fg-subtle)]"
          />
          <p className="text-xs text-muted-foreground">{t("form.metadataHint")}</p>
          {errors.metadata ? (
            <p id={metadataErrId} className="text-[13px] text-destructive" role="alert">
              {errors.metadata}
            </p>
          ) : null}
        </div>
      </section>

      {(props.renderFooter ?? true) ? (
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 rounded-md text-[13px]"
            onClick={props.onCancel}
            disabled={props.busy}
            data-testid="contact-form-cancel"
          >
            {t("form.cancel")}
          </Button>
          <Button
            type="submit"
            size="sm"
            className="h-9 rounded-lg px-4 text-[13px] hover:bg-primary-hover"
            disabled={props.busy}
            data-testid="contact-form-submit"
          >
            {props.mode === "create" ? t("form.submitCreate") : t("form.submit")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
